import { Link } from "react-router-dom";
import { Phone, MapPin, Clock, Facebook, Instagram } from "lucide-react";
import { useCart } from "@/App";

export default function Footer() {
  const { settings } = useCart();
  const s = settings || {};
  const addrLine1 = s.address_line1 || "578 Hyde Road";
  const addrLine2 = s.address_line2 || "Manchester M18 7EE";
  const phone = s.phone || "07404 133860";
  const phoneTel = phone.replace(/\s+/g, "");
  const hoursWeekday = s.hours_weekday || "Mon - Sat: 8AM - 10PM";
  const hoursWeekend = s.hours_weekend || "Sunday: 8AM - 8PM";
  return (
    <footer className="bg-slate-900 text-white">
      {/* Map section */}
      <div className="w-full h-64 map-container">
        <iframe
          title="Gorton Superstore Location"
          src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2375.5!2d-2.1755!3d53.4601!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x487bb3c61c8e8b95%3A0x0!2s578%20Hyde%20Road%2C%20Manchester%20M18%207EE!5e0!3m2!1sen!2suk!4v1234567890"
          width="100%"
          height="100%"
          style={{ border: 0 }}
          allowFullScreen=""
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          data-testid="store-map"
        />
      </div>

      {/* Footer content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8">
          {/* Brand */}
          <div className="lg:col-span-2">
            <div className="flex items-center gap-2 mb-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_grocery-pickup-1/artifacts/am06k7jp_FullLogo_NoBuffer%20%281%29.jpg" 
                alt="Gorton Superstore" 
                className="h-20 w-auto"
              />
            </div>
            <p className="text-slate-400 text-sm leading-relaxed">
              Your local Cash & Carry and Liquor Store. Fresh halal meat, quality groceries, and everyday essentials.
            </p>
            {/* Social Media Links */}
            <div className="flex gap-3 mt-4">
              <a
                href="https://facebook.com/gortonsuperstore"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-slate-800 rounded-full flex items-center justify-center hover:bg-emerald-600 transition-colors"
                data-testid="facebook-link"
              >
                <Facebook className="h-5 w-5 text-white" />
              </a>
              <a
                href="https://instagram.com/gortonsuperstore"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-slate-800 rounded-full flex items-center justify-center hover:bg-emerald-600 transition-colors"
                data-testid="instagram-link"
              >
                <Instagram className="h-5 w-5 text-white" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-semibold mb-4 text-lg">Shop</h4>
            <ul className="space-y-2">
              <li>
                <Link to="/products/halal-meat" className="text-slate-400 hover:text-emerald-400 transition-colors text-sm">
                  Halal Meat & Poultry
                </Link>
              </li>
              <li>
                <Link to="/products/off-licence" className="text-slate-400 hover:text-emerald-400 transition-colors text-sm">
                  Liquor Store
                </Link>
              </li>
              <li>
                <Link to="/products/fruits-and-vegetables" className="text-slate-400 hover:text-emerald-400 transition-colors text-sm">
                  Grocery & Frozen
                </Link>
              </li>
              <li>
                <Link to="/products?special=true" className="text-slate-400 hover:text-emerald-400 transition-colors text-sm">
                  Weekly Specials
                </Link>
              </li>
            </ul>
          </div>

          {/* Useful Links */}
          <div>
            <h4 className="font-semibold mb-4 text-lg">Useful Links</h4>
            <ul className="space-y-2">
              <li>
                <Link to="/faqs" className="text-slate-400 hover:text-emerald-400 transition-colors text-sm">
                  FAQs
                </Link>
              </li>
              <li>
                <Link to="/privacy-policy" className="text-slate-400 hover:text-emerald-400 transition-colors text-sm">
                  Privacy Policy
                </Link>
              </li>
              <li>
                <Link to="/terms" className="text-slate-400 hover:text-emerald-400 transition-colors text-sm">
                  Terms and Conditions
                </Link>
              </li>
              <li>
                <Link to="/refunds" className="text-slate-400 hover:text-emerald-400 transition-colors text-sm">
                  Refund and Returns
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact & Hours */}
          <div>
            <h4 className="font-semibold mb-4 text-lg">Contact</h4>
            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <MapPin className="h-5 w-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                <span className="text-slate-400 text-sm">
                  {addrLine1}<br />
                  {addrLine2}
                </span>
              </li>
              <li>
                <a 
                  href={`tel:${phoneTel}`}
                  className="flex items-center gap-3 text-slate-400 hover:text-emerald-400 transition-colors text-sm"
                  data-testid="footer-phone"
                >
                  <Phone className="h-5 w-5 text-emerald-400" />
                  {phone}
                </a>
              </li>
              <li className="flex items-start gap-3 mt-4">
                <Clock className="h-5 w-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                <div className="text-slate-400 text-sm">
                  <p className="font-medium text-white">Opening Hours</p>
                  <p>{hoursWeekday}</p>
                  <p>{hoursWeekend}</p>
                </div>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="border-t border-slate-800 mt-10 pt-6 flex flex-col sm:flex-row justify-between items-center gap-4">
          <p className="text-slate-500 text-sm">
            © {new Date().getFullYear()} Gorton Superstore. All rights reserved.
          </p>
          <div className="flex items-center gap-4">
            <a
              href="https://facebook.com/gortonsuperstore"
              target="_blank"
              rel="noopener noreferrer"
              className="text-slate-500 hover:text-emerald-400 transition-colors"
            >
              <Facebook className="h-5 w-5" />
            </a>
            <a
              href="https://instagram.com/gortonsuperstore"
              target="_blank"
              rel="noopener noreferrer"
              className="text-slate-500 hover:text-emerald-400 transition-colors"
            >
              <Instagram className="h-5 w-5" />
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}
